// ============================================================================
// CLEO'S SECRET — RecommendationBuilder
// File: src/pages/RecommendationBuilder.jsx
//
// Split-screen GP workspace for reviewing, editing, and approving a draft
// program recommendation.
//
//   LEFT pane  — clinical record snapshot for the patient under review.
//                Read-only. Quiet, scannable. SAND surface.
//   RIGHT pane — services workspace grouped by week. Each row exposes the
//                AI-drafted rationale, a GP rationale textarea, the
//                strongly-recommended toggle, the flag (fixed/customisable/
//                removable) select, and a remove button. Per-week "add
//                service" affordance at the foot of each card. PAP inset
//                cards inside the SAND surface.
//
// v1 deliberately excludes the package switcher — the page renders whichever
// package the recommendation currently points at. Switching packages will
// come later; for now Robyn supersedes-and-reruns if she wants a different
// package.
//
// Loads via /api/clinical-get-recommendation. Saves via
// /api/clinical-save-recommendation. Approves via
// /api/clinical-approve-recommendation. Approve always saves first.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { FF, FS, CINZEL, P, BtnPrimary, Spinner } from '../lib/design-tokens';
import RequireAdmin from '../components/RequireAdmin';

// ── Service-category labels (mirrors AdminPanel CatalogueView) ────────────
const CATEGORY_LABELS = {
  gp_consult:        'GP consult',
  allied_health:     'Allied health',
  nurse:             'Nurse',
  pathology:         'Pathology',
  supplement:        'Supplement',
  group:             'Group',
  workshop:          'Workshop',
  external_referral: 'External referral',
};

const FLAG_OPTIONS = ['fixed', 'customisable', 'removable'];
const FLAG_HINTS = {
  fixed:        'Patient cannot move or remove this service.',
  customisable: 'Patient can shift the week within the flex window.',
  removable:    'Patient can remove this service when enrolling.',
};

// ── Small atoms ───────────────────────────────────────────────────────────

function dollarsFromCents(c) {
  if (c == null) return '';
  return '$' + (c / 100).toLocaleString('en-AU', { maximumFractionDigits: 0 });
}

function StatusBadge({ status }) {
  const map = {
    draft:      { label: 'Draft',      bg: P.SAND2, fg: P.INK,   border: P.SAND3 },
    approved:   { label: 'Approved',   bg: P.GREEN_BG, fg: P.GREEN, border: P.GREEN },
    sent:       { label: 'Sent',       bg: P.GREEN_BG, fg: P.GREEN, border: P.GREEN },
    superseded: { label: 'Superseded', bg: P.SAND2, fg: P.TF,    border: P.SAND3 },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      fontFamily: FS,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      background: s.bg,
      color: s.fg,
      border: '1px solid ' + s.border,
    }}>{s.label}</span>
  );
}

function CategoryBadge({ category }) {
  const label = CATEGORY_LABELS[category] || category;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      fontFamily: FS,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      background: P.SAND,
      color: P.TF,
      border: '1px solid ' + P.SAND3,
    }}>{label}</span>
  );
}

function SectionLabel({ children, style }) {
  return (
    <div style={{
      fontFamily: CINZEL,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: P.BR,
      marginBottom: 14,
      ...style,
    }}>{children}</div>
  );
}

function Hairline() {
  return <div style={{ height: 1, background: P.SAND3, margin: '20px 0' }} />;
}

function Toggle({ value, onChange, label, hint }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={!!value}
        onChange={function(e) { onChange(e.target.checked); }}
        style={{ width: 18, height: 18 }}
      />
      <span style={{ fontFamily: FF, fontSize: 16, color: P.INK }}>{label}</span>
      {hint && (
        <span style={{ fontFamily: FF, fontSize: 13, fontStyle: 'italic', color: P.TF }}>{hint}</span>
      )}
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════════════════

// Outer wrapper. Gates on patients.is_admin via RequireAdmin so the inner
// page (and its data-fetching useEffects) only mount when the caller is
// confirmed admin. See src/components/RequireAdmin.jsx.
export default function RecommendationBuilder(props) {
  return (
    <RequireAdmin email={props.email} isAdmin={props.isAdmin} onNavigate={props.onNavigate}>
      <RecommendationBuilderInner
        onNavigate={props.onNavigate}
        recommendationId={props.recommendationId}
      />
    </RequireAdmin>
  );
}

function RecommendationBuilderInner(props) {
  const recommendationId = props.recommendationId;
  const onNavigate = props.onNavigate;

  var dataState   = useState(null);
  var data        = dataState[0]; var setData = dataState[1];
  var servicesState = useState([]); // editable working copy
  var services    = servicesState[0]; var setServices = servicesState[1];
  var loadingState  = useState(true);
  var loading     = loadingState[0]; var setLoading = loadingState[1];
  var errorState  = useState(null);
  var error       = errorState[0]; var setError = errorState[1];
  var savingState = useState(false);
  var saving      = savingState[0]; var setSaving = savingState[1];
  var approvingState = useState(false);
  var approving   = approvingState[0]; var setApproving = approvingState[1];
  var dirtyState  = useState(false);
  var dirty       = dirtyState[0]; var setDirty = dirtyState[1];
  var savedAtState = useState(null);
  var savedAt     = savedAtState[0]; var setSavedAt = savedAtState[1];
  var actionMessageState = useState(null);
  var actionMessage = actionMessageState[0]; var setActionMessage = actionMessageState[1];

  // ── Load on mount ───────────────────────────────────────────────────────
  useEffect(function() {
    if (!recommendationId) {
      setError('No recommendation id supplied.');
      setLoading(false);
      return;
    }
    var cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/clinical-get-recommendation?id=' + encodeURIComponent(recommendationId))
      .then(function(r) { return r.json(); })
      .then(function(payload) {
        if (cancelled) return;
        if (!payload || payload.error) {
          setError((payload && payload.error) || 'Failed to load recommendation.');
          setLoading(false);
          return;
        }
        setData(payload);
        setServices((payload.services || []).map(function(s) { return { ...s }; }));
        setLoading(false);
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error loading recommendation.');
        setLoading(false);
      });
    return function() { cancelled = true; };
  }, [recommendationId]);

  // ── Helpers to mutate the local services array ──────────────────────────
  function updateService(rowId, key, value) {
    setServices(function(prev) {
      return prev.map(function(s) {
        return s.id === rowId ? { ...s, [key]: value } : s;
      });
    });
    setDirty(true);
  }

  function removeService(rowId) {
    setServices(function(prev) { return prev.filter(function(s) { return s.id !== rowId; }); });
    setDirty(true);
  }

  function addServiceToWeek(week, serviceId) {
    if (!serviceId) return;
    const svc = (data.all_services || []).find(function(s) { return s.id === serviceId; });
    if (!svc) return;
    const tempRow = {
      id: 'tmp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
      __isNew: true,
      service_id: svc.id,
      services: svc,
      target_week: week,
      flex_window_weeks: 1,
      flag: 'customisable',
      strongly_recommended: false,
      ai_drafted_rationale: null,
      gp_rationale: null,
      sort_order: 999, // bumped on save
    };
    setServices(function(prev) { return prev.concat(tempRow); });
    setDirty(true);
  }

  // ── Build save payload (strip __isNew + tmp ids) ────────────────────────
  function buildSavePayload() {
    return services.map(function(s, idx) {
      const row = {
        service_id: s.service_id,
        target_week: s.target_week,
        flex_window_weeks: s.flex_window_weeks,
        flag: s.flag,
        strongly_recommended: !!s.strongly_recommended,
        gp_rationale: s.gp_rationale || null,
        sort_order: idx,
      };
      if (!s.__isNew && s.id && !String(s.id).startsWith('tmp-')) {
        row.id = s.id;
      }
      return row;
    });
  }

  async function handleSave() {
    if (!data) return;
    setSaving(true); setError(null); setActionMessage(null);
    try {
      const res = await fetch('/api/clinical-save-recommendation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: data.recommendation.id,
          services: buildSavePayload(),
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError((json && json.error) || 'Save failed.');
        setSaving(false);
        return false;
      }
      // Re-pull fresh state so id assignments + ai_drafted_rationale propagate
      const refreshed = await fetch('/api/clinical-get-recommendation?id=' + encodeURIComponent(data.recommendation.id))
        .then(function(r) { return r.json(); });
      if (refreshed && !refreshed.error) {
        setData(refreshed);
        setServices((refreshed.services || []).map(function(s) { return { ...s }; }));
      }
      setSaving(false);
      setDirty(false);
      setSavedAt(new Date());
      setActionMessage({ kind: 'ok', text: 'Draft saved.' });
      return true;
    } catch (e) {
      setError('Network error during save.');
      setSaving(false);
      return false;
    }
  }

  async function handleApprove() {
    if (!data) return;
    if (!window.confirm('Approve this recommendation and send to the patient?')) return;
    setApproving(true); setError(null); setActionMessage(null);
    // Always save first so we approve the latest state.
    if (dirty) {
      const ok = await handleSave();
      if (!ok) { setApproving(false); return; }
    }
    try {
      const res = await fetch('/api/clinical-approve-recommendation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: data.recommendation.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError((json && json.error) || 'Approval failed.');
        setApproving(false);
        return;
      }
      setApproving(false);
      setActionMessage({ kind: 'ok', text: 'Approved. The patient will see this plan in her portal.' });
      // Refresh status display
      setData(function(prev) {
        return prev ? { ...prev, recommendation: { ...prev.recommendation, ...json.recommendation } } : prev;
      });
    } catch (e) {
      setError('Network error during approval.');
      setApproving(false);
    }
  }

  // ── Loading + error states ──────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ background: P.SAND, minHeight: '100vh', padding: 'clamp(60px, 8vw, 120px)', textAlign: 'center' }}>
        <Spinner />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div style={{ background: P.SAND, minHeight: '100vh', padding: 'clamp(40px, 5vw, 80px)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: 36, background: P.PAP, border: '1px solid ' + P.SAND3 }}>
          <SectionLabel>Could not load recommendation</SectionLabel>
          <div style={{ fontFamily: FF, fontSize: 18, color: P.INK, lineHeight: 1.5 }}>{error}</div>
          <div style={{ marginTop: 24 }}>
            <BtnPrimary onClick={function() { onNavigate && onNavigate('clinical'); }}>
              Back to clinical dashboard
            </BtnPrimary>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const rec = data.recommendation;
  const patient = data.patient || {};
  const pattern = data.pattern || {};
  const pkg = data.package || {};
  const isEditable = rec.status === 'draft';

  // Group services by week
  const byWeek = {};
  services.forEach(function(s) {
    if (!byWeek[s.target_week]) byWeek[s.target_week] = [];
    byWeek[s.target_week].push(s);
  });
  const weeks = Object.keys(byWeek).map(Number).sort(function(a, b) { return a - b; });

  return (
    <div style={{ background: P.SAND, minHeight: '100vh' }}>

      {/* ─────────── Top bar ─────────── */}
      <div style={{
        background: P.PAP,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(16px, 1.8vw, 24px) clamp(20px, 3vw, 48px)',
      }}>
        <div style={{
          maxWidth: 1640, margin: '0 auto',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <button
              onClick={function() { onNavigate && onNavigate('clinical'); }}
              style={{
                fontFamily: FS, fontSize: 12, fontWeight: 700,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                background: 'transparent', color: P.TF,
                border: '1px solid ' + P.SAND3,
                padding: '8px 14px', cursor: 'pointer',
              }}>← Clinical dashboard</button>
            <div>
              <div style={{
                fontFamily: CINZEL, fontSize: 11, fontWeight: 700,
                letterSpacing: '0.32em', textTransform: 'uppercase', color: P.BR,
              }}>Recommendation builder</div>
              <div style={{
                fontFamily: FF, fontStyle: 'italic',
                fontSize: 'clamp(22px, 2.2vw, 28px)', color: P.INK, marginTop: 2,
              }}>
                {patient.first_name || ''} {patient.last_name || ''}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontFamily: FS, fontSize: 13, color: P.TF }}>
            <StatusBadge status={rec.status} />
            {dirty && (
              <span style={{ fontFamily: FF, fontStyle: 'italic', fontSize: 14, color: P.BR }}>
                · unsaved changes
              </span>
            )}
            {savedAt && !dirty && (
              <span style={{ fontFamily: FF, fontStyle: 'italic', fontSize: 14, color: P.GREEN }}>
                · saved at {savedAt.toLocaleTimeString('en-AU')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ─────────── Body: two columns ─────────── */}
      <div style={{
        maxWidth: 1640, margin: '0 auto',
        padding: 'clamp(20px, 3vw, 36px)',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
        gap: 'clamp(20px, 2.4vw, 32px)',
      }}>

        {/* ─────────── LEFT: clinical record snapshot ─────────── */}
        <div>
          {/* Pattern */}
          <div style={{
            background: P.PAP, border: '1px solid ' + P.SAND3,
            padding: 'clamp(22px, 2.2vw, 32px)', marginBottom: 18,
          }}>
            <SectionLabel>Clinical pattern</SectionLabel>
            <div style={{
              fontFamily: FF, fontSize: 'clamp(22px, 2.2vw, 28px)',
              color: P.INK, fontStyle: 'italic', lineHeight: 1.15,
              marginBottom: 10,
            }}>{pattern.name || 'Pattern not loaded'}</div>
            {pattern.profile_description && (
              <div style={{
                fontFamily: FF, fontSize: 16, color: P.TL, lineHeight: 1.6,
              }}>{pattern.profile_description}</div>
            )}
          </div>

          {/* GP narrative */}
          {data.pattern_narrative && (
            <div style={{
              background: P.PAP, border: '1px solid ' + P.SAND3,
              padding: 'clamp(22px, 2.2vw, 32px)', marginBottom: 18,
            }}>
              <SectionLabel>Approved narrative</SectionLabel>
              <div style={{
                fontFamily: FF, fontSize: 16, color: P.INK,
                lineHeight: 1.75, whiteSpace: 'pre-wrap',
              }}>{data.pattern_narrative}</div>
            </div>
          )}

          {/* Pathology */}
          {(data.pathology?.headline || (data.pathology?.flagged_markers || []).length > 0) && (
            <div style={{
              background: P.PAP, border: '1px solid ' + P.SAND3,
              padding: 'clamp(22px, 2.2vw, 32px)', marginBottom: 18,
            }}>
              <SectionLabel>Pathology</SectionLabel>
              {data.pathology.headline && (
                <div style={{
                  fontFamily: FF, fontStyle: 'italic',
                  fontSize: 18, color: P.INK, marginBottom: 14,
                  lineHeight: 1.4,
                }}>{data.pathology.headline}</div>
              )}
              {(data.pathology.key_findings || []).length > 0 && (
                <ul style={{
                  fontFamily: FF, fontSize: 15, color: P.TL,
                  lineHeight: 1.7, paddingLeft: 20, marginTop: 8, marginBottom: 16,
                }}>
                  {data.pathology.key_findings.map(function(f, i) {
                    return <li key={i} style={{ marginBottom: 6 }}>{f}</li>;
                  })}
                </ul>
              )}
              {(data.pathology.flagged_markers || []).length > 0 && (
                <div>
                  <div style={{
                    fontFamily: FS, fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: P.TF, marginTop: 12, marginBottom: 8,
                  }}>Flagged biomarkers</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FF, fontSize: 14 }}>
                    <tbody>
                      {data.pathology.flagged_markers.map(function(m, i) {
                        const flagColor =
                          m.flag === 'high' ? P.RED :
                          m.flag === 'low'  ? P.AMBER :
                          P.TF;
                        return (
                          <tr key={i} style={{ borderTop: i === 0 ? 'none' : '1px solid ' + P.SAND3 }}>
                            <td style={{ padding: '6px 0', color: P.INK }}>{m.marker_name}</td>
                            <td style={{ padding: '6px 8px', color: P.INK, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {m.value}{m.unit ? ' ' + m.unit : ''}
                            </td>
                            <td style={{ padding: '6px 0', color: P.TF, textAlign: 'right', fontSize: 12, fontStyle: 'italic' }}>
                              {m.reference_range || ''}
                            </td>
                            <td style={{ padding: '6px 0 6px 12px', color: flagColor, fontFamily: FS, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', textAlign: 'right' }}>
                              {m.flag}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Intake highlights — kept compact and quiet */}
          {data.intake_summary && (
            <div style={{
              background: P.PAP, border: '1px solid ' + P.SAND3,
              padding: 'clamp(22px, 2.2vw, 32px)', marginBottom: 18,
            }}>
              <SectionLabel>Intake highlights</SectionLabel>
              <pre style={{
                fontFamily: FS, fontSize: 12, color: P.TL,
                background: P.SAND, border: '1px solid ' + P.SAND3,
                padding: 14, lineHeight: 1.5,
                maxHeight: 360, overflow: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                margin: 0,
              }}>
                {JSON.stringify(data.intake_summary, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* ─────────── RIGHT: working surface ─────────── */}
        <div>
          {/* Package header */}
          <div style={{
            background: P.PAP, border: '1px solid ' + P.SAND3,
            borderTop: '3px solid ' + P.GOD,
            padding: 'clamp(22px, 2.2vw, 32px)', marginBottom: 18,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <SectionLabel style={{ marginBottom: 6 }}>Recommended package</SectionLabel>
                <div style={{
                  fontFamily: FF, fontStyle: 'italic',
                  fontSize: 'clamp(26px, 2.6vw, 32px)', color: P.INK, lineHeight: 1.1,
                  marginBottom: 6,
                }}>{pkg.display_name || ''}</div>
                <div style={{ fontFamily: FS, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', color: P.TF }}>
                  {pkg.bundled_membership_tier ? 'Bundled · ' + pkg.bundled_membership_tier : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: FF, fontSize: 'clamp(26px, 2.4vw, 32px)', color: P.INK }}>
                  {dollarsFromCents(pkg.total_price_cents)}
                </div>
                <div style={{ fontFamily: FS, fontSize: 11, color: P.TF, letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 4 }}>
                  {services.length} service{services.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            {rec.autofill_error && (
              <div style={{
                marginTop: 18, padding: '10px 14px',
                background: P.RED_BG, border: '1px solid ' + P.RED,
                fontFamily: FF, fontStyle: 'italic', fontSize: 14, color: P.RED,
              }}>
                Autofill error on creation: {rec.autofill_error}
              </div>
            )}
          </div>

          {/* Services by week */}
          {weeks.length === 0 && (
            <div style={{
              background: P.PAP, border: '1px dashed ' + P.SAND3,
              padding: 36, textAlign: 'center',
              fontFamily: FF, fontStyle: 'italic', color: P.TF,
            }}>
              No services attached yet.
            </div>
          )}

          {weeks.map(function(wk) {
            return (
              <WeekCard
                key={wk}
                week={wk}
                rows={byWeek[wk]}
                isEditable={isEditable}
                allServices={data.all_services || []}
                onUpdate={updateService}
                onRemove={removeService}
                onAdd={function(serviceId) { addServiceToWeek(wk, serviceId); }}
              />
            );
          })}

          {/* Action bar */}
          <div style={{
            position: 'sticky', bottom: 0,
            marginTop: 28,
            background: P.PAP, border: '1px solid ' + P.SAND3,
            borderTop: '3px solid ' + P.GOD,
            padding: 'clamp(18px, 2vw, 24px)',
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}>
            <div style={{ fontFamily: FF, fontSize: 14, color: P.TF, fontStyle: 'italic' }}>
              {!isEditable
                ? 'This recommendation is ' + rec.status + ' and locked from edits.'
                : (dirty ? 'You have unsaved changes.' : 'All changes saved.')}
              {actionMessage && (
                <span style={{
                  marginLeft: 14,
                  color: actionMessage.kind === 'ok' ? P.GREEN : P.RED,
                  fontStyle: 'italic',
                }}>{actionMessage.text}</span>
              )}
              {error && (
                <span style={{ marginLeft: 14, color: P.RED, fontStyle: 'italic' }}>{error}</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleSave}
                disabled={!isEditable || saving || approving}
                style={{
                  padding: '14px 22px',
                  fontFamily: FS, fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  background: 'transparent', color: P.INK,
                  border: '1px solid ' + P.GOD,
                  cursor: (!isEditable || saving || approving) ? 'not-allowed' : 'pointer',
                  opacity: (!isEditable || saving || approving) ? 0.5 : 1,
                }}>{saving ? 'Saving' : 'Save draft'}</button>
              <BtnPrimary
                onClick={handleApprove}
                disabled={!isEditable || saving || approving || services.length === 0}>
                {approving ? 'Approving' : 'Approve and send to patient'}
              </BtnPrimary>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// WeekCard — one card per week, contains all services scheduled for that week
// ═══════════════════════════════════════════════════════════════════════════

function WeekCard({ week, rows, isEditable, allServices, onUpdate, onRemove, onAdd }) {
  var pickerState = useState('');
  var addServiceId = pickerState[0]; var setAddServiceId = pickerState[1];

  return (
    <div style={{
      background: P.PAP, border: '1px solid ' + P.SAND3,
      padding: 'clamp(20px, 2vw, 28px)', marginBottom: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <SectionLabel style={{ marginBottom: 0 }}>Week {week}</SectionLabel>
        <div style={{ fontFamily: FS, fontSize: 12, color: P.TF, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
          {rows.length} item{rows.length === 1 ? '' : 's'}
        </div>
      </div>

      {rows.map(function(s) {
        return (
          <ServiceRow
            key={s.id}
            row={s}
            isEditable={isEditable}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        );
      })}

      {isEditable && (
        <div style={{
          marginTop: 14, padding: 12,
          background: P.SAND, border: '1px dashed ' + P.SAND3,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <select
            value={addServiceId}
            onChange={function(e) { setAddServiceId(e.target.value); }}
            style={{
              flex: 1, padding: '8px 10px',
              fontFamily: FF, fontSize: 15, color: P.INK,
              background: P.PAP, border: '1px solid ' + P.SAND3,
            }}>
            <option value="">+ Add a service to week {week}</option>
            {allServices.map(function(svc) {
              return <option key={svc.id} value={svc.id}>{svc.name}</option>;
            })}
          </select>
          <button
            onClick={function() { if (addServiceId) { onAdd(addServiceId); setAddServiceId(''); } }}
            disabled={!addServiceId}
            style={{
              padding: '8px 14px',
              fontFamily: FS, fontSize: 12, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              background: addServiceId ? P.GOD : P.SAND2,
              color: addServiceId ? P.PAP : P.TF,
              border: '1px solid ' + (addServiceId ? P.GOD : P.SAND3),
              cursor: addServiceId ? 'pointer' : 'not-allowed',
            }}>Add</button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ServiceRow — one inline editor per service inside a WeekCard
// ═══════════════════════════════════════════════════════════════════════════

function ServiceRow({ row, isEditable, onUpdate, onRemove }) {
  const svc = row.services || {};

  function useAiAsGp() {
    if (row.ai_drafted_rationale) {
      onUpdate(row.id, 'gp_rationale', row.ai_drafted_rationale);
    }
  }

  return (
    <div style={{
      marginBottom: 14, padding: 'clamp(14px, 1.6vw, 20px)',
      background: P.SAND, border: '1px solid ' + P.SAND3,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: FF, fontSize: 'clamp(17px, 1.5vw, 19px)',
            color: P.INK, fontWeight: 500, marginBottom: 6,
          }}>{svc.name || '(unknown service)'}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <CategoryBadge category={svc.category} />
            {svc.duration_minutes != null && (
              <span style={{ fontFamily: FS, fontSize: 12, color: P.TF }}>
                {svc.duration_minutes} min
              </span>
            )}
            <span style={{ fontFamily: FS, fontSize: 12, color: P.TF }}>
              {row.flex_window_weeks > 0 ? '± ' + row.flex_window_weeks + ' wk flex' : 'no flex'}
            </span>
          </div>
        </div>
        {isEditable && (
          <button
            onClick={function() { if (window.confirm('Remove this service from the recommendation?')) onRemove(row.id); }}
            style={{
              padding: '6px 12px',
              fontFamily: FS, fontSize: 11, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              background: 'transparent', color: P.RED,
              border: '1px solid ' + P.SAND3, cursor: 'pointer',
            }}>Remove</button>
        )}
      </div>

      {/* AI rationale (read-only context) */}
      {row.ai_drafted_rationale && (
        <div style={{
          marginBottom: 12, padding: '10px 14px',
          background: P.PAP, border: '1px solid ' + P.SAND3,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6,
          }}>
            <div style={{
              fontFamily: FS, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase', color: P.BR,
            }}>AI-drafted rationale</div>
            {isEditable && (
              <button
                onClick={useAiAsGp}
                style={{
                  fontFamily: FS, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  background: 'transparent', color: P.BR,
                  border: 'none', padding: 0, cursor: 'pointer',
                }}>Use as GP rationale →</button>
            )}
          </div>
          <div style={{
            fontFamily: FF, fontStyle: 'italic',
            fontSize: 15, color: P.TL, lineHeight: 1.55,
          }}>{row.ai_drafted_rationale}</div>
        </div>
      )}

      {/* GP rationale (editable) */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontFamily: FS, fontSize: 10, fontWeight: 700,
          letterSpacing: '0.22em', textTransform: 'uppercase', color: P.TF,
          marginBottom: 6,
        }}>GP rationale</div>
        <textarea
          value={row.gp_rationale || ''}
          onChange={function(e) { onUpdate(row.id, 'gp_rationale', e.target.value); }}
          disabled={!isEditable}
          rows={3}
          placeholder={isEditable ? 'Edit, paste from the AI draft, or write fresh.' : ''}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '10px 12px',
            fontFamily: FF, fontSize: 15, color: P.INK, lineHeight: 1.55,
            background: P.PAP, border: '1px solid ' + P.SAND3,
            outline: 'none', resize: 'vertical',
          }}
        />
      </div>

      {/* Controls row */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <Toggle
          label="Strongly recommended"
          value={row.strongly_recommended}
          onChange={function(v) { if (isEditable) onUpdate(row.id, 'strongly_recommended', v); }}
        />
        <div>
          <div style={{
            fontFamily: FS, fontSize: 10, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase', color: P.TF,
            marginBottom: 4,
          }}>Flag</div>
          <select
            value={row.flag}
            onChange={function(e) { if (isEditable) onUpdate(row.id, 'flag', e.target.value); }}
            disabled={!isEditable}
            style={{
              padding: '6px 8px',
              fontFamily: FF, fontSize: 14, color: P.INK,
              background: P.PAP, border: '1px solid ' + P.SAND3,
            }}>
            {FLAG_OPTIONS.map(function(f) {
              return <option key={f} value={f}>{f}</option>;
            })}
          </select>
          <div style={{
            fontFamily: FF, fontStyle: 'italic', fontSize: 12, color: P.TF, marginTop: 4, maxWidth: 360,
          }}>{FLAG_HINTS[row.flag]}</div>
        </div>
      </div>
    </div>
  );
}
